<template>
  <div>
    <!-- Step 1: template + file -->
    <div v-if="step === 'upload'">
      <b-alert show variant="light" class="border">
        <ol class="mb-0 pl-3">
          <li>Descarga la plantilla (ya trae tus sucursales, proveedores e insumos activos).</li>
          <li>Captura <b>una fila por insumo</b>. Las filas con la misma sucursal, proveedor y fecha se agrupan en una sola orden.</li>
          <li>Sube el archivo y revisa la vista previa antes de confirmar.</li>
        </ol>
      </b-alert>

      <b-button variant="outline-primary" size="sm" class="mb-3" :disabled="downloading" @click="downloadTemplate">
        <b-spinner v-if="downloading" small></b-spinner>
        <i v-else class="fas fa-file-excel mr-1"></i> Descargar plantilla
      </b-button>

      <b-form-group label="Archivo Excel (.xlsx):">
        <b-form-file
          v-model="file"
          accept=".xlsx"
          placeholder="Selecciona o arrastra el archivo..."
          browse-text="Buscar"
          :disabled="loading"
        ></b-form-file>
      </b-form-group>

      <div class="text-right">
        <b-button variant="success" :disabled="!file || loading" @click="runPreview">
          <b-spinner v-if="loading" small></b-spinner>
          Validar archivo
        </b-button>
      </div>
    </div>

    <!-- Step 2: preview -->
    <div v-else-if="step === 'preview'">
      <!-- Errors: todo o nada -->
      <template v-if="!preview.ok">
        <b-alert show variant="danger">
          <b>No se puede importar.</b> Corrige los {{ preview.errors.length }} error(es) y vuelve a subir el archivo.
          No se creará ninguna orden hasta que el archivo esté limpio.
        </b-alert>
        <b-table small striped :items="preview.errors" :fields="errorFields" responsive class="mb-0"></b-table>
      </template>

      <!-- OK: summary + confirm -->
      <template v-else>
        <b-alert show variant="success">
          Se crearán <b>{{ preview.orders_to_create }}</b> órdenes de compra con
          <b>{{ preview.line_count }}</b> líneas en total.
        </b-alert>

        <b-alert v-if="preview.warnings && preview.warnings.length" show variant="warning">
          <b>{{ preview.warnings.length }} aviso(s):</b>
          <ul class="mb-0 mt-1 pl-3" style="max-height: 140px; overflow-y: auto;">
            <li v-for="(wn, i) in preview.warnings" :key="i"><small>Fila {{ wn.row }}: {{ wn.message }}</small></li>
          </ul>
        </b-alert>

        <b-table
          small striped hover
          :items="preview.summary"
          :fields="summaryFields"
          responsive
          class="mb-3"
          style="max-height: 320px; overflow-y: auto;"
        >
          <template #cell(amount_untaxed)="data">{{ formatMoney(data.item.amount_untaxed) }}</template>
        </b-table>
      </template>

      <div class="d-flex justify-content-between">
        <b-button variant="link" :disabled="loading" @click="resetToUpload">← Elegir otro archivo</b-button>
        <b-button v-if="preview.ok" variant="success" :disabled="loading" @click="runCommit">
          <b-spinner v-if="loading" small></b-spinner>
          Confirmar y crear {{ preview.orders_to_create }} órdenes
        </b-button>
      </div>
    </div>

    <!-- Step 3: done -->
    <div v-else-if="step === 'done'">
      <b-alert show variant="success" class="text-center">
        <i class="fas fa-check-circle fa-2x mb-2 d-block"></i>
        <b>{{ result.created }}</b> órdenes de compra creadas correctamente (en estado borrador).
      </b-alert>
      <div class="text-right">
        <b-button variant="outline-secondary" size="sm" class="mr-2" @click="resetToUpload">Importar otro archivo</b-button>
        <b-button variant="primary" @click="$emit('done', result)">Cerrar</b-button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "BulkImportPurchaseOrders",
  data() {
    return {
      step: "upload",
      file: null,
      loading: false,
      downloading: false,
      preview: { ok: false, errors: [], warnings: [], summary: [], orders_to_create: 0, line_count: 0, token: "" },
      result: { created: 0, purchase_order_ids: [] },
      errorFields: [
        { key: "row", label: "Fila" },
        { key: "message", label: "Error" },
      ],
      summaryFields: [
        { key: "name", label: "Orden" },
        { key: "sucursal_id", label: "Sucursal" },
        { key: "provider_id", label: "Proveedor" },
        { key: "date_planned", label: "Entrega" },
        { key: "lines", label: "Líneas" },
        { key: "amount_untaxed", label: "Subtotal" },
      ],
    };
  },
  methods: {
    resetToUpload() {
      this.step = "upload";
      this.file = null;
      this.preview = { ok: false, errors: [], warnings: [], summary: [], orders_to_create: 0, line_count: 0, token: "" };
    },
    downloadTemplate() {
      let that = this;
      this.downloading = true;
      axios
        .get("/purchaseorders/import/template", { responseType: "blob", headers: { Accept: "*/*" } })
        .then(function (response) {
          const blob = new Blob([response.data]);
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          let filename = "plantilla_ordenes_compra.xlsx";
          const cd = response.headers["content-disposition"];
          if (cd && cd.includes("filename")) {
            const m = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(cd);
            if (m != null && m[1]) filename = m[1].replace(/['"]/g, "");
          }
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(function (err) {
          that.$emit("error", "No se pudo descargar la plantilla: " + that.errText(err));
        })
        .finally(function () {
          that.downloading = false;
        });
    },
    runPreview() {
      if (!this.file) return;
      let that = this;
      this.loading = true;
      const form = new FormData();
      form.append("file", this.file);
      axios
        .post("/purchaseorders/import?mode=preview", form, { headers: { "Content-Type": "multipart/form-data" } })
        .then(function (res) {
          that.preview = Object.assign(
            { ok: false, errors: [], warnings: [], summary: [], orders_to_create: 0, line_count: 0, token: "" },
            res.data
          );
          that.step = "preview";
        })
        .catch(function (err) {
          that.$emit("error", that.errText(err));
        })
        .finally(function () {
          that.loading = false;
        });
    },
    runCommit() {
      if (!this.preview.token) return;
      let that = this;
      this.loading = true;
      axios
        .post("/purchaseorders/import?mode=commit", { token: this.preview.token })
        .then(function (res) {
          that.result = res.data;
          that.step = "done";
          that.$emit("created", res.data);
        })
        .catch(function (err) {
          that.$emit("error", that.errText(err));
        })
        .finally(function () {
          that.loading = false;
        });
    },
    formatMoney(v) {
      const n = Number(v || 0);
      return n.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
    },
    errText(err) {
      if (err && err.response && err.response.data) {
        if (typeof err.response.data === "string") return err.response.data;
        return JSON.stringify(err.response.data);
      }
      return (err && err.message) || "Error desconocido";
    },
  },
};
</script>
